import ChevronDownThinIcon from '@assets/icons/chevron-down-thin.svg';
import ChevronRightThinIcon from '@assets/icons/chevron-right-thin.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef, useState } from 'react';
import { ActionToastContent } from '@/components/action-toast-content';
import { Analytics } from '@/utils/analytics';
import { AnalyticsHubFeedScreens } from '@/components/insights-hub/types';
import { Animated, Platform, Text, TouchableOpacity, View } from 'react-native';
import { APP_MODAL_ROUTES, HIDDEN_ALERTS_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { ClearAllAlertsConfirmationActionSheet } from '@/components/insights-hub/clear-all-alerts-confirmation-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { HubCardData } from '@/hooks/insights-hub/types';
import { HubFeedCardsList } from '@/components/insights-hub/hub-feed-cards-list';
import { StackNavigationProp } from '@react-navigation/stack';
import { TYPOGRAPHY } from '@/styles';
import { useAnalyticsAlertActions } from '@/hooks/insights-hub/use-analytics-alerts-actions';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useHiddenAlertsData } from '@/hooks/insights-hub/use-hidden-alerts-data';
import { useHover } from 'react-native-web-hooks';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useLayoutContext } from '@/contexts/layout-context';
import { useNavigation } from '@react-navigation/native';
import { useToasts } from '@/contexts/toast-context';

const PAGINATED_ALERTS_COUNT = 10;

export const HubHiddenAlerts: React.FC = () => {
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const { showErrorToast, showToast } = useToasts();
  const { data, loading, error } = useHiddenAlertsData();
  const { selectedRestaurantRelationship } = useAuthenticatedUserContext();
  const { deleteHiddenAlerts, undeleteHiddenAlerts } = useAnalyticsAlertActions(
    selectedRestaurantRelationship.supplierUUID,
  );
  const { navigateToRelevantScreen, navigateToViewAccount } =
    useHubCardNavigation();
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  const { navigate } =
    useNavigation<
      StackNavigationProp<AppModalStackParams, APP_MODAL_ROUTES.HiddenAlerts>
    >();

  const clearAllRef = useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  const isHovered = useHover(clearAllRef);

  const [isExpanded, setIsExpanded] = useState(false);
  // internal state to manage the number of displayed alerts at a given time
  const [displayedCount, setDisplayedCount] = useState(PAGINATED_ALERTS_COUNT);

  const rotateAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(rotateAnim, {
      duration: 150,
      toValue: isExpanded ? 1 : 0,
      useNativeDriver: true,
    }).start();
  }, [isExpanded, rotateAnim]);

  const rotate = rotateAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  if (!isSmallLayout && (!data || data.length === 0 || error || loading)) {
    return null;
  }

  const cardCount = data?.length ?? 0;
  const viewMoreEnabled = cardCount > displayedCount;

  const paginatedAlerts = data?.slice(0, displayedCount) ?? [];

  const onPress = () => {
    // On mobile devices and on mobile web, navigate to the screen, on desktop/tablet
    // devices, open the hidden alerts right below inline on the same Insights Hub screen
    if (isSmallLayout) {
      navigate(APP_MODAL_ROUTES.HiddenAlerts, {
        screen: HIDDEN_ALERTS_ROUTES.Base,
      });
    } else {
      setIsExpanded((expanded) => !expanded);
    }

    Analytics.track('insights_hub.hub_hidden_alerts_section.press', {
      cardCount,
      platform: Platform.OS,
    });
  };

  const onPressViewMore = () => {
    Analytics.track('insights_hub.hub_hidden_alerts_section.view_more.press', {
      cardCount,
      platform: Platform.OS,
    });
    // increase the number of displayed alerts by PAGINATED_ALERTS_COUNT,
    // but check it does not exceed the total number of available hidden alerts (cardCount)
    setDisplayedCount((prev) =>
      Math.min(prev + PAGINATED_ALERTS_COUNT, cardCount),
    );
  };

  const onPressCard = (cardData: HubCardData) => {
    navigateToViewAccount(cardData);
    Analytics.track('insights_hub.alert.press', {
      alertType: cardData.card_type,
      restaurantUUID: cardData.restaurant_uuid,
      screen: AnalyticsHubFeedScreens.HiddenAlerts,
    });
  };

  const onPressCardSupportInfo = (cardData: HubCardData) => {
    navigateToRelevantScreen(cardData);
    Analytics.track('insights_hub.alert.support_info.press', {
      alertType: cardData.card_type,
      restaurantUUID: cardData.restaurant_uuid,
      screen: AnalyticsHubFeedScreens.HiddenAlerts,
    });
  };

  const onConfirmClearAll = async () => {
    if (!data || data.length === 0) {
      return;
    }

    try {
      // Close the confirmation sheet, then await the mutation. This allows us to make
      // an optimistic update, while still handling possible errors raised by the mutation.
      setShowClearConfirmation(false);
      await deleteHiddenAlerts(data.map((card) => card.alert_uuid));
      Analytics.track('insights_hub.alert.delete_all.press', {
        alertUUIDs: data.map((card) => card.alert_uuid),
        platform: Platform.OS,
      });

      // show the confirmation toast with the undo button
      showToast({
        autoCloseTimeout: 0,
        content: (onClose) => (
          <ActionToastContent
            buttonProps={{
              children: i18n.t('undo'),
              onPress: async () => {
                onClose();
                await undeleteHiddenAlerts(data, 0);
                Analytics.track('insights_hub.alert.delete_all.undo.press', {
                  alertUUIDs: data.map((card) => card.alert_uuid),
                  platform: Platform.OS,
                });
              },
              trackingData: null,
            }}
            textContent={i18n.t('dsr_analytics.alerts_removed')}
          />
        ),
      });
    } catch (e) {
      showErrorToast();
    }
  };

  return (
    <View style={styles.base}>
      <TouchableOpacity onPress={onPress} style={styles.container}>
        <View collapsable={false}>
          <Text
            style={[
              TYPOGRAPHY.h3,
              styles.title,
              isExpanded ? styles.titleExpanded : null,
            ]}
          >
            {i18n.t('hidden')}
          </Text>
          {!isExpanded ? (
            <Text style={[TYPOGRAPHY.medium, styles.subtitle]}>
              {i18n.t('insights_hub.hidden_alerts_section')}
            </Text>
          ) : null}
        </View>
        <View style={styles.row}>
          {isSmallLayout ? (
            // mobile: always show count + right‑pointing chevron
            <>
              <Text style={[TYPOGRAPHY.medium, styles.subtitle]}>
                {cardCount > 99 ? '🌶' : cardCount}
              </Text>
              <ChevronRightThinIcon stroke={theme.sk.color.icon.primary} />
            </>
          ) : (
            // desktop/tablet: show count only when collapsed, Clear all only when expanded, plus flip chevron
            <>
              {isExpanded ? (
                <TouchableOpacity
                  onPress={() => setShowClearConfirmation(true)}
                  ref={clearAllRef}
                  style={[isHovered ? styles.hover : null]}
                >
                  <Text style={[TYPOGRAPHY.pBold, styles.clearText]}>
                    {i18n.t('clear_all')}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text style={[TYPOGRAPHY.medium, styles.subtitle]}>
                  {cardCount > 99 ? '🌶' : cardCount}
                </Text>
              )}
              <Animated.View style={{ transform: [{ rotate }] }}>
                <ChevronDownThinIcon stroke={theme.sk.color.icon.primary} />
              </Animated.View>
            </>
          )}
        </View>
      </TouchableOpacity>
      {isExpanded ? (
        <>
          <HubFeedCardsList
            alerts={paginatedAlerts}
            onPress={onPressCard}
            onPressSupportInfo={onPressCardSupportInfo}
          />
          {viewMoreEnabled &&
          cardCount &&
          paginatedAlerts.length < cardCount ? (
            <TouchableOpacity
              onPress={onPressViewMore}
              style={styles.viewMoreButton}
            >
              <Text style={[TYPOGRAPHY.pBold, styles.viewMoreText]}>
                {i18n.t('view_more')}
              </Text>
            </TouchableOpacity>
          ) : null}
        </>
      ) : null}
      {showClearConfirmation ? (
        <ClearAllAlertsConfirmationActionSheet
          onClose={() => setShowClearConfirmation(false)}
          onConfirm={onConfirmClearAll}
        />
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
    borderRadius: 12,
  },
  clearText: {
    color: theme.sk.color.text.primary,
    fontSize: 14,
    lineHeight: 21,
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 66,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  hover: {
    opacity: 0.8,
  },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 14,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
    fontSize: 14,
  },
  title: {
    color: theme.sk.color.text.primary,
    fontSize: 16,
  },
  titleExpanded: {
    fontSize: 21,
  },
  viewMoreButton: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.sk.color.bg.primary.disabled,
    borderRadius: 48,
    justifyContent: 'center',
    marginBottom: 16,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  viewMoreText: {
    color: theme.sk.color.text.secondary,
    fontSize: 12,
    letterSpacing: 0.01,
    lineHeight: 18,
    textAlign: 'center',
  },
}));
